import { Avatar, Box, Stack, Typography } from '@mui/material'
import { useState } from 'react'

import {
  ItemImageAreaFragment,
  ItemThumbnailType,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { ItemReversibleThumbnail } from '@/components/organisms'
import { useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'

export type ItemImageAreaProps = {
  item: Maybe<ItemImageAreaFragment>
  loading: boolean
  noItem?: boolean
  avatarUri?: string
  possessionNumber?: number
}

export const ItemImageArea: Organism<ItemImageAreaProps> = ({
  item,
  loading,
  noItem,
  children,
  possessionNumber,
  avatarUri,
}) => {
  const [reverse, setReverse] = useState(false)
  return (
    <OrganismFrame
      component={Stack}
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flex={1}
    >
      <Box sx={{ position: 'relative' }}>
        <ItemReversibleThumbnail
          loading={loading}
          flipProps={{ isFlipped: reverse }}
          imageAreaProps={{
            thumbnail: item?.thumbnail,
            size:
              item?.thumbnailType === ItemThumbnailType.Square
                ? 'large-square'
                : 'large',
            onClick: () => {
              if (!item?.thumbnail.back?.uri) return
              setReverse(prev => !prev)
            },
            noItem,
          }}
        />
      </Box>
      <ItemDescriptionCell
        item={item}
        possessionNumber={possessionNumber}
        avatarUri={avatarUri}
      />
      {children}
    </OrganismFrame>
  )
}

type ItemDescriptionCellProps = {
  item: ItemImageAreaProps['item']
  possessionNumber?: number
  avatarUri?: string
}
const ItemDescriptionCell: Cell<ItemDescriptionCellProps> = ({
  item,
  possessionNumber,
  avatarUri,
}) => {
  const { t } = useTranslation('collectionItemDetail')
  return (
    <CellFrame>
      <Stack sx={{ py: 2 }} alignItems="center">
        <Typography variant="h5">{item?.title}</Typography>
        {item?.sku && (
          <Typography variant="body2">{item.sku.unitName}</Typography>
        )}
      </Stack>
      <Stack direction="row" justifyContent="center" spacing={4}>
        {item?.rarity && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="overline" color="text.hint">
              {t('rarity')}
            </Typography>
            {item?.rarityIconImage?.smallUri && (
              <Avatar
                variant="square"
                src={item.rarityIconImage.smallUri}
                sx={{ height: 12, width: 12 }}
              />
            )}
            <Typography variant="overline">
              {item?.rarity?.name ? t(item?.rarity.name) : '-'}
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="overline" color="text.hint">
            {t('possessionNumber')}
          </Typography>
          {!!avatarUri && (
            <Avatar src={avatarUri} sx={{ height: 12, width: 12 }} />
          )}
          <Typography variant="overline">{possessionNumber ?? 0}</Typography>
        </Stack>
      </Stack>
    </CellFrame>
  )
}
